"use client";

import useTranslate from "@app/_hooks/useTranslate";
import DiscountIcon from "@app/_icons/DiscountIcon/DiscountIcon";
import FormPopup from "@forms/formPopup";
import type { Form, ProductPageInfoFragment } from "@server/gql/graphql";
import type { LoopFetchProductResult } from "@server/utils/parseLoop/types";
import dynamic from "next/dynamic";

import type { MarketConfiguration } from "@/config/markets/const";
import { cn } from "@/lib/utils";
import { PropsWithChildren } from "react";

const FREE_SHIPPING_USP_ID = "17QcX4I20l9IBbKzNyrQQY";
const RETURN_USP_ID = "25O3y4al4oaGTkpslLMLdJ";

const ProductPageFreeShippingUspItem = dynamic(
  () => import("./ProductPageFreeShippingUspItem"),
);
const ProductPageReturnUspItem = dynamic(
  () => import("./ProductPageReturnUspItem"),
);

export default function ProductPageUsps({
  className,
  marketConfig,
  productPageInfo,
  currentVariant,
  children,
}: PropsWithChildren<{
  className?: string;
  marketConfig: MarketConfiguration;
  productPageInfo?: ProductPageInfoFragment;
  currentVariant: LoopFetchProductResult["currentVariant"];
}>) {
  const t = useTranslate();
  return (
    <>
      <ul
        className={cn(
          "divide-gray-150 border-gray-150 grid grid-flow-row divide-y border text-base",
          className,
        )}
      >
        {productPageInfo?.uspsCollection?.items?.map((usp) => {
          if (!usp) return null;

          switch (usp.sys.id) {
            case FREE_SHIPPING_USP_ID:
              if (marketConfig.freeShippingEnabled) {
                return (
                  <ProductPageFreeShippingUspItem
                    key={usp.sys.id}
                    usp={usp}
                    marketConfig={marketConfig}
                    currentVariantPrice={currentVariant?.price ?? 0}
                  />
                );
              }
              return null;
            case RETURN_USP_ID:
              return (
                <ProductPageReturnUspItem
                  key={usp.sys.id}
                  usp={usp}
                  marketCode={marketConfig.code}
                  locale={marketConfig.locale}
                />
              );
          }
        })}
        {productPageInfo?.newsletterForm && (
          <li className="flex items-start gap-4 p-4">
            <DiscountIcon className="h-5 w-5 shrink-0 text-black" />
            <div>
              <span className="mr-1">{t("newCustomer")} </span>
              <FormPopup
                className="inline"
                buttonContent={t("getFifteenPercentDiscount")}
                form={productPageInfo?.newsletterForm as Form}
              />
            </div>
          </li>
        )}
        {children}
      </ul>
    </>
  );
}
